import type { ButtonProps } from "@/types";

/**
 * Button Component
 * Interactive button with multiple variants
 * Can be used as a button or link
 */
export const Button = ({
  children,
  href,
  onClick,
  variant = 'primary',
  size = 'md',
  className = '',
  type = 'button',
  disabled = false,
}: ButtonProps) => {
  // Base classes for all buttons
  const baseClasses = 'inline-flex items-center justify-center font-space-mono font-bold text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 clip-corner-tr';

  // Variant classes using custom Tailwind colors
  const variantClasses = {
    primary: 'bg-primary text-black hover:bg-primary/90 focus:ring-primary',
    secondary: 'bg-secondary text-white hover:bg-secondary/90 focus:ring-secondary',
    outline: 'border-2 border-primary text-primary hover:bg-primary hover:text-white focus:ring-primary',
    black: 'bg-black text-white hover:bg-gray-900 focus:ring-black',
    white: 'bg-white text-black hover:bg-gray-50 focus:ring-gray-300',
  };

  // Responsive size classes
  const sizeClasses = {
    sm: 'px-3 py-2 md:px-4 md:py-2.5',
    md: 'px-4 py-2.5 md:px-6 md:py-3',
    lg: 'px-6 py-3 md:px-8 md:py-4',
  };

  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';
  const allClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${disabledClasses} ${className}`;

  // Render as link if href is provided
  if (href) {
    return (
      <a href={href} className={allClasses}>
        {children}
      </a>
    );
  }

  // Render as button
  return (
    <button type={type} onClick={onClick} className={allClasses} disabled={disabled}>
      {children}
    </button>
  );
};

export default Button;
